/**
 * ChatMessageNodeRemapper
 *
 * Bridges Firestore wire JSON ↔ `ChatMessageNodeDto` ↔ `ChatMessageNodeEntity`,
 * following the `fromJson(): Dto` + `fromDto(): Entity` convention codified by
 * `chatMessageRemapper.ts` and `data-layer.md`.
 * Flutter source: lib/data/models/chat_message_node.dart:31-49.
 *
 * The DTO keeps the snake_case Firestore wire keys (`created_timestamp`,
 * `last_content`); the entity is camelCase (`createdTimestamp`, `lastContent`).
 * `fromJson` reads the raw doc into the DTO without renaming; `fromDto` performs
 * the snake_case → camelCase translation. Defaults mirror Flutter:
 * `created_timestamp ?? 0`, `blocked ?? false`. `users` is not read by Flutter on
 * this model but is required on the web entity, so it defaults to `[]`. The doc id
 * == `chatId` (doc lives at `/message_nodes/{groupChatId}`), so the entity carries
 * no `documentId`.
 */

import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { ChatMessageNodeDto } from '@/data/dtos/chat/chatMessageNodeDto';
import type { JsonObject } from '@/data/types/json';

export class ChatMessageNodeRemapper {
  /**
   * Parse a Firestore `/message_nodes/{groupChatId}` document into a
   * `ChatMessageNodeDto`, keeping the snake_case wire keys (`created_timestamp`,
   * `last_content`) — no camelCasing happens here (that is `fromDto`'s job).
   */
  static fromJson(data: JsonObject): ChatMessageNodeDto {
    return {
      chatId: (data['chatId'] as string | undefined) ?? '',
      idFrom: (data['idFrom'] as string | undefined) ?? '',
      idTo: (data['idTo'] as string | undefined) ?? '',
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
      created_timestamp: (data['created_timestamp'] as number | undefined) ?? 0,
      last_content: (data['last_content'] as string | undefined) ?? '',
      type: (data['type'] as number | undefined) ?? 0,
      nameUser1: (data['nameUser1'] as string | undefined) ?? '',
      nameUser2: (data['nameUser2'] as string | undefined) ?? '',
      seenByPeer: (data['seenByPeer'] as boolean | undefined) ?? false,
      blocked: (data['blocked'] as boolean | undefined) ?? false,
      users: (data['users'] as string[] | undefined) ?? [],
    };
  }

  static fromJsonList(dataList: JsonObject[]): ChatMessageNodeDto[] {
    return dataList.map(ChatMessageNodeRemapper.fromJson);
  }

  static toJson(dto: ChatMessageNodeDto): JsonObject {
    return { ...dto } as JsonObject;
  }

  /**
   * Build a `ChatMessageNodeEntity` from a `ChatMessageNodeDto`, translating the
   * snake_case wire keys to the camelCase entity fields
   * (`created_timestamp` → `createdTimestamp`, `last_content` → `lastContent`) so
   * the downstream peer-name decrypt path writes `lastContent`, not `last_content`.
   */
  static fromDto(dto: ChatMessageNodeDto): ChatMessageNodeEntity {
    return {
      chatId: dto.chatId,
      idFrom: dto.idFrom,
      idTo: dto.idTo,
      timestamp: dto.timestamp,
      createdTimestamp: dto.created_timestamp,
      lastContent: dto.last_content,
      type: dto.type,
      nameUser1: dto.nameUser1,
      nameUser2: dto.nameUser2,
      seenByPeer: dto.seenByPeer,
      blocked: dto.blocked,
      users: dto.users,
    };
  }

  static toDto(entity: ChatMessageNodeEntity): ChatMessageNodeDto {
    return {
      chatId: entity.chatId,
      idFrom: entity.idFrom,
      idTo: entity.idTo,
      timestamp: entity.timestamp,
      created_timestamp: entity.createdTimestamp,
      last_content: entity.lastContent,
      type: entity.type,
      nameUser1: entity.nameUser1,
      nameUser2: entity.nameUser2,
      seenByPeer: entity.seenByPeer,
      blocked: entity.blocked,
      users: entity.users,
    };
  }
}
